import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthContext';
import { Button } from '@/components/ui/button';
import { BookOpen, Library, Compass, Users, MessageCircle, LogOut, Bell, UsersRound, Sparkles, ShieldAlert } from 'lucide-react';
import { DASHBOARD } from '@/constants/testIds';
import BookTokFeed from '@/components/BookTokFeed';
import VibeQuiz, { VIBE_META } from '@/components/VibeQuiz';
import axios from 'axios';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [notifications, setNotifications] = useState({ unread_messages: 0, pending_requests: 0, upcoming_meetings: 0, total: 0 });
  const [showQuiz, setShowQuiz] = useState(false);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/summary`,
        { withCredentials: true }
      );
      setNotifications(data);
    } catch (error) {
      // silent
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { icon: Users, label: 'Profile', path: '/profile', testId: DASHBOARD.profileLink, badge: 0 },
    { icon: Library, label: 'My Library', path: '/library', testId: DASHBOARD.libraryLink, badge: 0 },
    { icon: Compass, label: 'Discover', path: '/discover', testId: DASHBOARD.discoverLink, badge: 0 },
    { icon: UsersRound, label: 'Book Clubs', path: '/bookclubs', testId: 'dashboard-bookclubs-link', badge: notifications.upcoming_meetings },
    { icon: Users, label: 'Connections', path: '/connections', testId: DASHBOARD.connectionsLink, badge: notifications.pending_requests },
    { icon: MessageCircle, label: 'Messages', path: '/messages', testId: DASHBOARD.messagesLink, badge: notifications.unread_messages }
  ];

  return (
    <div data-testid={DASHBOARD.container} className="min-h-screen bg-[#FDFBF7] paper-texture relative overflow-hidden">
      {/* Cozy bookshelf backdrop (visible but softened) */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: 'url(/bookshelf-backdrop.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.55,
        }}
      />
      {/* Cream veil to soften the backdrop so cards read cleanly */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(253,251,247,0.45) 0%, rgba(253,251,247,0.25) 45%, rgba(253,251,247,0.55) 100%)',
        }}
      />

      <nav data-testid={DASHBOARD.navBar} className="relative z-20 bg-white/80 backdrop-blur-xl border-b border-[#E1D9CD] sticky top-0">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-[#6B7A4F]" />
              <span className="text-xl font-bold text-[#2D2A26]">Bookish Bestie</span>
              <span className="hidden md:inline text-[10px] tracking-[0.2em] uppercase text-[#6B7A4F] ml-2 opacity-70 border-l border-[#E1D9CD] pl-2">
                Built for Bookish
              </span>
            </div>
            <div className="flex items-center gap-4">
              {/* Notification Bell */}
              <button
                data-testid="dashboard-notification-bell"
                className="relative p-2 rounded-full hover:bg-[#E2E6D5]/50 transition-colors"
                onClick={() => notifications.pending_requests > 0 ? navigate('/connections') : navigate('/messages')}
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5 text-[#5C5955]" strokeWidth={1.5} />
                {notifications.total > 0 && (
                  <span
                    data-testid="dashboard-notification-badge"
                    className="absolute -top-0.5 -right-0.5 bg-[#6B7A4F] text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                  >
                    {notifications.total > 9 ? '9+' : notifications.total}
                  </span>
                )}
              </button>
              <span className="text-[#5C5955] hidden sm:inline">Hello, {user?.name}</span>
              {user?.role === 'admin' && (
                <Button
                  data-testid="dashboard-admin-link"
                  onClick={() => navigate('/admin')}
                  variant="outline"
                  className="border-[#6B7A4F] text-[#6B7A4F] hover:bg-[#6B7A4F] hover:text-white"
                >
                  <ShieldAlert className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              )}
              <Button
                data-testid={DASHBOARD.logoutBtn}
                onClick={handleLogout}
                variant="outline"
                className="border-[#6B7A4F] text-[#6B7A4F] hover:bg-[#6B7A4F] hover:text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="relative z-10 container mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-light text-[#2D2A26] mb-4">
            Welcome back, <span className="font-bold">{user?.name}</span>
          </h1>
          <p className="text-lg text-[#5C5955]">
            Your reading journey awaits
          </p>
          {(notifications.total > 0 || notifications.upcoming_meetings > 0) && (
            <p className="text-sm text-[#6B7A4F] mt-3 font-medium" data-testid="dashboard-notification-summary">
              {notifications.pending_requests > 0 && `${notifications.pending_requests} pending request${notifications.pending_requests > 1 ? 's' : ''}`}
              {notifications.pending_requests > 0 && notifications.unread_messages > 0 && ' · '}
              {notifications.unread_messages > 0 && `${notifications.unread_messages} unread message${notifications.unread_messages > 1 ? 's' : ''}`}
              {(notifications.pending_requests > 0 || notifications.unread_messages > 0) && notifications.upcoming_meetings > 0 && ' · '}
              {notifications.upcoming_meetings > 0 && `${notifications.upcoming_meetings} upcoming meeting${notifications.upcoming_meetings > 1 ? 's' : ''} this week`}
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {menuItems.map((item) => (
            <button
              key={item.path}
              data-testid={item.testId}
              onClick={() => navigate(item.path)}
              className="relative glass-card p-8 rounded-2xl hover:-translate-y-2 transition-all duration-300 group text-left"
            >
              <div className="w-14 h-14 bg-[#E2E6D5] rounded-full flex items-center justify-center mb-4 group-hover:bg-[#6B7A4F] transition-colors">
                <item.icon className="w-7 h-7 text-[#6B7A4F] group-hover:text-white transition-colors" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-[#2D2A26]">{item.label}</h3>
              {item.badge > 0 && (
                <span className="absolute top-4 right-4 bg-[#6B7A4F] text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center px-2">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Vibe Quiz CTA / Result badge */}
        <div className="max-w-5xl mx-auto mt-10">
          {user?.reader_vibe && VIBE_META[user.reader_vibe] ? (
            <button
              onClick={() => setShowQuiz(true)}
              data-testid="vibe-badge"
              className="w-full glass-card p-5 rounded-2xl flex items-center justify-between gap-4 hover:-translate-y-1 transition-all group text-left"
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center shadow-md flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${VIBE_META[user.reader_vibe].color}, ${VIBE_META[user.reader_vibe].color}CC)` }}
                >
                  <span className="text-2xl">{VIBE_META[user.reader_vibe].emoji}</span>
                </div>
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-[#6B7A4F] font-bold">Your Reader Vibe</p>
                  <h3 className="text-xl font-bold text-[#2D2A26]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {VIBE_META[user.reader_vibe].name}
                  </h3>
                  <p className="text-sm text-[#5C5955] hidden sm:block">{VIBE_META[user.reader_vibe].tagline}</p>
                </div>
              </div>
              <span className="text-xs uppercase tracking-wider text-[#6B7A4F] font-semibold whitespace-nowrap opacity-70 group-hover:opacity-100">
                View picks →
              </span>
            </button>
          ) : (
            <button
              onClick={() => setShowQuiz(true)}
              data-testid="vibe-quiz-cta"
              className="w-full glass-card p-6 rounded-2xl flex items-center gap-5 hover:-translate-y-1 transition-all group text-left relative overflow-hidden"
              style={{
                background:
                  'linear-gradient(120deg, rgba(107, 122, 79,0.12) 0%, rgba(112,140,110,0.10) 100%)',
              }}
            >
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#6B7A4F] to-[#434E30] flex items-center justify-center shadow-md flex-shrink-0">
                <Sparkles className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <p className="text-xs tracking-[0.2em] uppercase text-[#6B7A4F] font-bold">New · Take 60 seconds</p>
                <h3 className="text-xl font-bold text-[#2D2A26]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  What's your Reader Vibe?
                </h3>
                <p className="text-sm text-[#5C5955]">
                  5 questions → your archetype + 3 personalized book picks.
                </p>
              </div>
              <span className="text-sm font-semibold text-[#6B7A4F] whitespace-nowrap group-hover:translate-x-1 transition-transform">
                Start →
              </span>
            </button>
          )}
        </div>

        <BookTokFeed />
      </div>

      {showQuiz && (
        <VibeQuiz
          onClose={() => setShowQuiz(false)}
          onComplete={() => refreshUser && refreshUser()}
          currentVibe={user?.reader_vibe}
          firstName={user?.name ? user.name.split(' ')[0] : ''}
        />
      )}
    </div>
  );
};

export default Dashboard;
